"use client";

import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

import { StoreLocatorLayoutSwitcher } from "@locators/components/shared/components/StoreLocatorLayoutSwitcher";
import { useFlotiqStorageConfig } from "@locators/components/shared/hooks/useFlotiqStorageConfig";
import type { StoreDetailsDataProps, StoreExtendedParams, StoreIcons } from "@locators/components/shared/types";
import { getConvertedAddress } from "@locators/components/shared/utils/getTransformedMarkers";
import { StoreDetailsV1 } from "@locators/components/StoreDetails/components/StoreDetailsV1/StoreDetailsV1";
import { StoreDetailsV2 } from "@locators/components/StoreDetails/components/StoreDetailsV2/StoreDetailsV2";

import { useConfig } from "@jti/app-context/useConfig";
import { Loader } from "@jti/core-components/components/Loader";
import { FlotiqApi } from "@jti/rest-api";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import styles from "./StoreDetails.module.scss";

export type StoreDetailsProps = {
  storeIcons?: StoreIcons[];
  storeLocatorUrl?: string;
  componentLevelLanguage?: string;
};

type StoreDetailsDataFromUrlProps = StoreExtendedParams & { shopId: string; defaultZoom?: string };

export type StoreDetailsComponentProps = {
  isHeaderAddressVisible?: boolean;
  storeDetails: StoreDetailsDataProps;
  storeIcons?: StoreIcons[];
  storeLocatorUrl?: string;
  componentLevelLanguage?: string;
  storeDetailsPageUrl?: string;
};

export const getStoreDetailsDataFromUrl = (searchParams: URLSearchParams): StoreDetailsDataFromUrlProps => {
  const entries = Array.from(searchParams.entries());

  const booleanParamKeys = new Set([
    "isOpeningHoursEnabled",
    "isOpeningHoursBreaksEnabled",
    "isAvailableDevicesEnabled",
    "isAvailableSticksEnabled",
    "isMultiBrandSticksEnabled",
    "isStoreTypeEnabled",
    "isAvailableServicesEnabled",
    "isGeneralAnnouncementEnabled",
    "isVendingMachineEnabled",
    "isVendingMachineTipEnabled",
    "isNearbyShopsEnabled",
    "isAsianAddressFormatEnabled",
  ]);

  return Object.fromEntries(
    entries.map(([key, value]) => {
      if (booleanParamKeys.has(key)) {
        return [key, value === "true"];
      }
      return [key, value];
    })
  ) as unknown as StoreDetailsDataFromUrlProps;
};

export const StoreDetails: FC<StoreDetailsProps> = ({ storeIcons, storeLocatorUrl, componentLevelLanguage }) => {
  const { __ } = useTranslations();
  const { locationServiceMapProvider } = useConfig();

  const [storeDetails, setStoreDetails] = useState<StoreDetailsDataProps>();
  const { flotiqConfig } = useFlotiqStorageConfig();
  const isHeaderAddressVisible = locationServiceMapProvider !== "naver";

  const updateStoreLocatorData = useCallback(async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const storeDetailsDataFromUrl: StoreDetailsDataFromUrlProps = getStoreDetailsDataFromUrl(searchParams);

    const flotiqApi = FlotiqApi.init(flotiqConfig?.flotiqApiKey, flotiqConfig?.contentType);
    const store = await flotiqApi.getStoreById(storeDetailsDataFromUrl.shopId);

    const address = getConvertedAddress(store, storeDetailsDataFromUrl.isAsianAddressFormatEnabled);
    setStoreDetails({
      store: { ...store, address },
      defaultZoom: storeDetailsDataFromUrl.defaultZoom ? Number(storeDetailsDataFromUrl.defaultZoom) : undefined,
      isOpeningHoursEnabled: storeDetailsDataFromUrl.isOpeningHoursEnabled,
      isOpeningHoursBreaksEnabled: storeDetailsDataFromUrl.isOpeningHoursBreaksEnabled,
      isAvailableDevicesEnabled: storeDetailsDataFromUrl.isAvailableDevicesEnabled,
      isAvailableSticksEnabled: storeDetailsDataFromUrl.isAvailableSticksEnabled,
      isMultiBrandSticksEnabled: storeDetailsDataFromUrl.isMultiBrandSticksEnabled,
      isGeneralAnnouncementEnabled: storeDetailsDataFromUrl.isGeneralAnnouncementEnabled,
      isAvailableServicesEnabled: storeDetailsDataFromUrl.isAvailableServicesEnabled,
      isStoreTypeEnabled: storeDetailsDataFromUrl.isStoreTypeEnabled,
      isVendingMachineEnabled: storeDetailsDataFromUrl.isVendingMachineEnabled,
      isVendingMachineTipEnabled: storeDetailsDataFromUrl.isVendingMachineTipEnabled,
      isNearbyShopsEnabled: storeDetailsDataFromUrl.isNearbyShopsEnabled,
    });
  }, [setStoreDetails]);

  useEffect(() => {
    let ignore = false;
    if (!ignore) {
      updateStoreLocatorData();
    }
    return () => {
      ignore = true;
    };
  }, [updateStoreLocatorData]);

  if (!storeDetails) {
    return (
      <div className={styles.loaderContainer}>
        <Loader data-testid="storeDetailsLoader" />
      </div>
    );
  }

  return (
    <div className={styles.storeDetailsContainer}>
      <StoreLocatorLayoutSwitcher layout="v1">
        <StoreDetailsV1
          storeDetails={storeDetails}
          isHeaderAddressVisible={isHeaderAddressVisible}
          storeIcons={storeIcons}
          componentLevelLanguage={componentLevelLanguage}
        ></StoreDetailsV1>
      </StoreLocatorLayoutSwitcher>
      <StoreLocatorLayoutSwitcher layout="v2">
        <StoreDetailsV2
          storeDetails={storeDetails}
          storeIcons={storeIcons}
          storeLocatorUrl={storeLocatorUrl}
          componentLevelLanguage={componentLevelLanguage}
        ></StoreDetailsV2>
      </StoreLocatorLayoutSwitcher>
    </div>
  );
};
